import emailAddresses from 'email-addresses';
import * as _ from 'lodash-es';
import React, { useRef, useState } from 'react';
import getSuggestedEmail from './getSuggestedEmail';
import * as styles from './InvitationUserSelect.module.scss';
import InvitationUserSelectInput from './InvitationUserSelectInput';
import InvitationUserSelectMultiValue from './InvitationUserSelectMultiValue';
import InvitationUserSelectOption from './InvitationUserSelectOption';
import useAppSelector from '../app/redux/useAppSelector';
import Select from '../components/Select';
import { MOD_KEY } from '../constants/os';
import searchHighlight from '../utils/search/searchHighlight';

export type InvitationUserSelectOptionType = {
  label: string;
  value: string;
};

interface InvitationUserSelectProps {
  value: InvitationUserSelectOptionType[];
  onChange: (newValue: InvitationUserSelectOptionType[]) => void;
  placeholder?: string;
  onSubmit?: () => void;
  disabled?: boolean;
}

export const inputToOptions: (
  rawInput: string,
) => InvitationUserSelectOptionType[] | null = (rawInput) => {
  const input = rawInput.trim();
  const parsed =
    emailAddresses.parseAddressList(input) ||
    emailAddresses.parseAddressList({ input, addressListSeparator: '\n' }) ||
    emailAddresses.parseAddressList({ input, addressListSeparator: '\t' }) ||
    emailAddresses.parseAddressList(
      input
        .split(/\t|\n|\s/)
        .filter((part) => part.includes('@'))
        .join(','),
    );

  const options =
    parsed
      ?.flatMap((result) =>
        result.type === 'group' ? result.addresses : result,
      )
      ?.map<InvitationUserSelectOptionType>(({ name, address }) => ({
        label: name || address,
        value: address,
      })) ?? null;

  return options;
};

const getInviteLabel = (organizationEmail: string) => (email: string) => (
  <>
    Invite {searchHighlight(getSuggestedEmail(email, organizationEmail), email)}
  </>
);

const InvitationUserSelect = ({
  onChange,
  value,
  placeholder,
  onSubmit,
  disabled,
}: InvitationUserSelectProps) => {
  const organizationEmail = useAppSelector(
    (state) => state.session.organization.email,
  );
  const [inputValue, setInputValue] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [userOptions, setUserOptions] = useState<
    InvitationUserSelectOptionType[]
  >([]);
  const [valueBeingUpdated, setValueBeingUpdated] = useState<string | null>(
    null,
  );
  const [updatingInputValue, setUpdatingInputValue] = useState('');

  const onChangeValueBeingUpdated = (newValueToUpdate: string | null) => {
    setValueBeingUpdated(newValueToUpdate);

    setUpdatingInputValue(newValueToUpdate ?? '');
  };

  const isInputProcessedThroughOnPaste = useRef(false);

  const handleNewUserInput = (input: string) => {
    const newOptions: InvitationUserSelectOptionType[] | null =
      inputToOptions(input);

    if (!newOptions) {
      return false;
    }

    if (valueBeingUpdated) {
      if (newOptions.length === 1 && newOptions[0]) {
        setUserOptions(
          _.compact(
            userOptions.map((option) =>
              option.value === valueBeingUpdated ? newOptions[0] : option,
            ),
          ),
        );
        onChange(
          _.compact(
            value.map((option) =>
              option.value === valueBeingUpdated ? newOptions[0] : option,
            ),
          ),
        );
      }
      setValueBeingUpdated(null);
      return true;
    }

    setUserOptions(_.uniqBy([...userOptions, ...newOptions], 'value'));
    onChange(_.uniqBy([...value, ...newOptions], 'value'));
    return true;
  };

  const handleChange = (
    newValue: readonly InvitationUserSelectOptionType[],
  ) => {
    // Update an existing value and its option
    if (valueBeingUpdated) {
      const newUsers = _.differenceBy(newValue, value, 'value');
      const newUser = newUsers.length === 1 ? newUsers[0] : null;
      if (newUser) {
        onChange([
          ...value.map((oldUser) =>
            oldUser.value === valueBeingUpdated ? newUser : oldUser,
          ),
        ]);
        return;
      }
    }
    onChange([...newValue]);
  };

  return (
    <div
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          if (e[MOD_KEY]) {
            onSubmit?.();
          } else {
            e.preventDefault();
            e.stopPropagation();
          }
        }
      }}
    >
      <Select<InvitationUserSelectOptionType, true, false>
        simpleValue={false}
        value={value}
        onChange={handleChange}
        className={styles.select}
        creatable
        isMulti
        isDisabled={disabled}
        isClearable={false}
        name="invitations"
        options={userOptions}
        components={{
          DropdownIndicator: () => null,
          MultiValue: InvitationUserSelectMultiValue,
          Option: InvitationUserSelectOption,
          Input: valueBeingUpdated ? () => null : InvitationUserSelectInput,
        }}
        // @ts-expect-error
        updateValueProps={{
          valueBeingUpdated,
          onChangeValueBeingUpdated,
          updatingInputValue,
          setUpdatingInputValue,
          handleBlur: () => setIsMenuOpen(false),
        }}
        placeholder={placeholder ?? 'sam@yourcompany.com'}
        formatCreateLabel={getInviteLabel(organizationEmail)}
        onCreateOption={(input: string) => {
          handleNewUserInput(getSuggestedEmail(input, organizationEmail));
        }}
        onPaste={(e: React.ClipboardEvent) => {
          const input = e.clipboardData.getData('text/plain');
          isInputProcessedThroughOnPaste.current = handleNewUserInput(input);
        }}
        inputValue={valueBeingUpdated ? updatingInputValue : inputValue}
        onInputChange={(newInputValue: string) => {
          if (valueBeingUpdated) {
            onChangeValueBeingUpdated(newInputValue);
            return;
          }
          if (isInputProcessedThroughOnPaste.current) {
            setInputValue('');
            isInputProcessedThroughOnPaste.current = false;
            return;
          }

          setInputValue(newInputValue);
        }}
        menuIsOpen={isMenuOpen || !!valueBeingUpdated}
        onMenuOpen={() => setIsMenuOpen(true)}
        onMenuClose={() => setIsMenuOpen(false)}
        isValidNewOption={(input: string) => {
          const suggestedEmail = getSuggestedEmail(input, organizationEmail);
          return (
            !value.find((v) => v.value === suggestedEmail) &&
            !userOptions.find((option) => option.value === suggestedEmail) &&
            !!inputToOptions(suggestedEmail)
          );
        }}
        noOptionsMessage={() => 'Enter a valid email to invite…'}
      />
    </div>
  );
};

export default InvitationUserSelect;
